<template>
    <a class="skip-link" href="#main-content">{{ $t('accessibility.skip-to-content', 'Skip to main content') }}</a>
    <Panel
        v-if="$route.meta.isCabinet"
        :title="title"
        ref="panel"
        @updated="panelUpdated"
        @langChanged="langChanged"
        @exposeToggle="childToggle = $event"
        @exposeSidebarShow="showSidebar = $event"
    />
    <main id="main-content" tabindex="-1">
    <router-view v-slot="{ Component }">
        <component
            ref="view"
            :is="Component"
            :inbox="info.inbox"
            :outbox="info.outbox"
            :current-plan="info.plan"
            :balance="info.balance"
            :lang="lang"
            @setPageTitle="setPageTitle"
            @updateCounters="updateCounters"
            @updateBalance="updateBalance"
            @updateMeta="updateMeta"
            @setDescMeta="setDescMeta"
            @getUnreadNotices="getUnreadNotices"
        />
    </router-view>
    </main>
    <notifications position="bottom right" width="400px">
        <template #body="props">
            <div class="my-notification">
                <div class="row">
                    <div v-if="props.item.type" class="col-md-2 d-flex align-items-center justify-content-end">
                        <i class="bi fs-3" :class="{ 'bi-exclamation-triangle-fill text-danger': props.item.type==='error', 'bi-check-circle-fill text-success': props.item.type==='success', 'bi-exclamation-triangle-fill text-warning': props.item.type==='warn' }"></i>
                    </div>
                    <div v-if="props.item.type" class="col-md-8">
                        <h5 class="title">{{ props.item.title }}</h5>
                        <div v-html="props.item.text"/>
                    </div>
                    <div v-if="!props.item.type" class="col-md-10">
                        <h5 class="title">{{ props.item.title }}</h5>
                        <div v-html="props.item.text"/>
                    </div>
                    <div class="col-md-2 d-flex align-items-center justify-content-end">
                        <button type="button" class="btn btn-link close" aria-label="Close notification" @click="props.close">
                            <i class="bi bi-x-lg fs-4" aria-hidden="true"></i>
                        </button>
                    </div>
                </div>
            </div>
        </template>
    </notifications>
    <Modal />
</template>

<script>
    import { driver } from "driver.js"
    import "driver.js/dist/driver.css"
    import { nextTick } from 'vue'
    import Panel from './components/cabinet/Panel.vue'
    import Modal from './components/Modal.vue'

    export default {
        data: () => ({
            title: `KDP`,
            metaDescription: '',
            lang: '',
            info: {
                inbox: 0,
                outbox: 0,
                balance: 0,
                plan: ''
            },
            isBlurred: JSON.parse(localStorage.getItem('isBlurred')),
            childToggle: null,
            isMobile: false,
            TOUR_ROUTES: {},
            pendingTour: null
        }),
        components: {
            Panel,
            Modal
        },
        methods: {
            langChanged(lang) {
                this.lang = lang
                if(this.$refs.view.langChange) this.$refs.view.langChange()
            },
            setPageTitle(title) {
                this.title = title
            },
            updateCounters() {
                this.$refs.panel.updateCounters()
            },
            updateBalance() {
                this.$refs.panel.updateBalance()
            },
            panelUpdated({ key, value }) {
                if(key in this.info) {
                    if(key != 'plan') {
                        this.info[key] = value
                    } else {
                        this.info[key] = value.name
                    }
                }
            },
            updateMeta(data = {}) {
                const title = document.title;

                if(!Object.keys(data).length) {
                    data = {
                        "meta": [
                            {
                                "name": "description",
                                "content": this.$t('head.meta.description')
                            },
                            {
                                "property": "og:title",
                                "content": this.$t('head.meta.og:title')
                            },
                            {
                                "property": "og:description",
                                "content": this.$t('head.meta.description')
                            },
                            {
                                name: 'og:title',
                                content: this.$t('head.meta.og:title')
                            },
                            {
                                name: 'og:url',
                                content: window.location.href
                            },
                            {
                                name: 'og:description',
                                content: this.$t('head.meta.description')
                            },
                            {
                                name: 'og:image',
                                content: process.env.VUE_APP_URL + '/emails/images/ogimage.png'
                            },
                            {
                                property: 'og:url',
                                content: window.location.href
                            },
                            {
                                property: 'og:image',
                                content: process.env.VUE_APP_URL + '/emails/images/ogimage.png'
                            }
                        ]
                    }

                    if (title.includes('[DEV]')) {
                        data.title = '[DEV] ' + this.$t('head.title')
                    } else if (title.includes('[STAGE]')) {
                        data.title  = '[STAGE] ' + this.$t('head.title')
                    } else if (title.includes('[LOCAL]')) {
                        data.title  = '[LOCAL] ' + this.$t('head.title')
                    } else {
                        data.title  = this.$t('head.title')
                    }
                }

                this.updateMetaTags(data)
            },
            setDescMeta(content) {
                let metaTags = document.querySelectorAll('meta[name="description"]')

                if(metaTags.length > 0) {
                    metaTags.forEach((metaTag) => {
                        metaTag.remove()
                    })
                }

                const textarea = document.createElement('textarea');
                const plainText = content.replace(/<[^>]+>/g, '');
                const firstSentence = plainText.split(/[.!?]\s/)[0] + '.';
                textarea.innerHTML = firstSentence;
                const decodedDescription = textarea.value;

                let descTag = document.createElement('meta')

                descTag.setAttribute('name', 'description')
                descTag.setAttribute('content', decodedDescription)

                document.head.appendChild(descTag)
            },
            updateOrganizationType() {
                this.$refs.panel.updateOrganizationType()
            },
            getUnreadNotices() {
                this.$refs.panel.getUnreadNotices()
            },
            toBlur() {
                document.addEventListener('keydown', (event) => {
                    if (event.ctrlKey && event.altKey && event.key === 'x') {
                        this.isBlurred = !this.isBlurred

                        this.applyBlurState()
                    }
                })
            },
            applyBlurState() {
                // Add or remove the 'blurred' class based on the current blur state
                document.querySelectorAll('.to-blur').forEach(element => {
                    if (this.isBlurred) {
                        element.classList.add('blurred')
                        this.$store.commit('setBlur', true)
                    } else {
                        element.classList.remove('blurred')
                        this.$store.commit('setBlur', false)
                    }
                })

                document.querySelectorAll('.to-hide').forEach(element => {
                    if (this.isBlurred) {
                        element.classList.add('hidden')
                    } else {
                        element.classList.remove('hidden')
                    }
                })
            },
            startProfileTour() {
                this.hideBitrixWidget()

                const steps = [
                    {
                        popover: {
                            title: this.$t('tours.profile.step_1.title'),
                            description: this.$t('tours.profile.step_1.description'),
                            position: 'bottom'
                        }
                    },

                    this.safeStep('#profile-phone-action', {
                        title: this.$t('tours.profile.step_2.title'),
                        description: this.$t('tours.profile.step_2.description'),
                        position: 'bottom'
                    }),


                    this.safeStep('#change-address', {
                        title: this.$t('tours.profile.step_4.title'),
                        description: this.$t('tours.profile.step_4.description'),
                        position: 'bottom',
                        onNextClick: () => {
                            if (!this.isMobile) {
                                this.childToggle(0)
                            }
                            tour.moveNext()
                        }
                    }),

                    this.isMobile
                        ? this.safeStep('#menu-button-mobile', {
                            title: this.$t('tours.profile.step_4_mobile.title'),
                            description: this.$t('tours.profile.step_4_mobile.description'),
                            position: 'bottom',
                            onNextClick: () => {
                                this.showSidebar()
                                this.childToggle(0)
                                tour.moveNext()
                            }
                        })
                        : null,

                    this.safeStep(this.isMobile ? '#claims-tab-mobile' : '#claims-tab', {
                        title: this.$t('tours.profile.step_5.title'),
                        description: this.$t('tours.profile.step_5.description'),
                        position: 'bottom',
                        onPrevClick: () => {
                            if (this.isMobile) {
                                this.showSidebar()
                            }
                            setTimeout(() => {
                                this.childToggle(0)
                            }, 50)

                            tour.movePrevious()
                        },
                        onNextClick: async () => {
                            await this.$api.completeTour('profile')
                            this.showBitrixWidget()
                            tour.destroy()
                        }
                    })
                ].filter(Boolean)

                const tour = driver({
                    showProgress: true,
                    progressText: '{{current}} / {{total}}',
                    allowClose: false,
                    disableActiveInteraction: true,
                    overlayOpacity: 0.65,
                    animate: true,
                    smoothScroll: true,
                    nextBtnText: this.$t('tours.popover.btn-next'),
                    prevBtnText: this.$t('tours.popover.btn-prev'),
                    doneBtnText: this.$t('tours.popover.btn-done'),
                    steps
                })

                tour.drive()
            },
            startClaimsTour() {
                this.hideBitrixWidget()
                const steps = [
                    {
                        popover: {
                            title: this.$t('tours.claims.step_1.title'),
                            description: this.$t('tours.claims.step_1.description'),
                            position: 'bottom'
                        }
                    },

                    this.safeStep('#new-claim-form', {
                        title: this.$t('tours.claims.step_2.title'),
                        description: this.$t('tours.claims.step_2.description'),
                        position: 'bottom'
                    }),

                    this.safeStep('#claim-type-select', {
                        title: this.$t('tours.claims.step_3.title'),
                        description: this.$t('tours.claims.step_3.description'),
                        position: 'bottom'
                    }),

                    this.safeStep('#recipient-input', {
                        title: this.$t('tours.claims.step_4.title'),
                        description: this.$t('tours.claims.step_4.description'),
                        position: 'bottom'
                    }),

                    this.safeStep('#occurrence-date-input', {
                        title: this.$t('tours.claims.step_5.title'),
                        description: this.$t('tours.claims.step_5.description'),
                        position: 'bottom'
                    }),

                    this.safeStep('#amount-owed-input', {
                        title: this.$t('tours.claims.step_6.title'),
                        description: this.$t('tours.claims.step_6.description'),
                        position: 'bottom'
                    }),

                    this.safeStep('#template-fields', {
                        title: this.$t('tours.claims.step_7.title'),
                        description: this.$t('tours.claims.step_7.description'),
                        position: 'bottom'
                    }),

                    this.safeStep('#notification-method', {
                        title: this.$t('tours.claims.step_8.title'),
                        description: this.$t('tours.claims.step_8.description'),
                        position: 'bottom'
                    }),

                    this.safeStep('#save-button', {
                        title: this.$t('tours.claims.step_9.title'),
                        description: this.$t('tours.claims.step_9.description'),
                        position: 'bottom',
                        onNextClick: async () => {
                            if(tour.isLastStep()) {
                                await this.$api.completeTour('claims')
                                this.showBitrixWidget()
                                tour.destroy()
                            }
                            else {
                                tour.moveNext()
                            }
                        }
                    }),

                    this.safeStep('#send-button', {
                        title: this.$t('tours.claims.step_10.title'),
                        description: this.$t('tours.claims.step_10.description'),
                        position: 'bottom',
                        onNextClick: async () => {
                            await this.$api.completeTour('claims')
                            this.showBitrixWidget()
                            tour.destroy()
                        }
                    }),

                ].filter(Boolean)

                const tour = driver({
                    showProgress: true,
                    progressText: '{{current}} / {{total}}',
                    allowClose: true,
                    disableActiveInteraction: true,
                    overlayOpacity: 0.65,
                    animate: true,
                    smoothScroll: true,
                    nextBtnText: this.$t('tours.popover.btn-next'),
                    prevBtnText: this.$t('tours.popover.btn-prev'),
                    doneBtnText: this.$t('tours.popover.btn-done'),
                    steps
                })

                tour.drive()
            },
            startAddRecipientTour() {
                this.hideBitrixWidget()
                const steps = [
                    this.safeStep('#modal-content', {
                        title: this.$t('tours.add-recipient.step_2.title'),
                        description: this.$t('tours.add-recipient.step_2.description'),
                        side: "left",
                        align: 'start'
                    }),

                    this.safeStep('#iin', {
                        title: this.$t('tours.add-recipient.step_2.title'),
                        description: this.$t('tours.add-recipient.step_2.description'),
                        position: 'bottom'
                    }),

                    this.safeStep('#phone', {
                        title: this.$t('tours.add-recipient.step_3.title'),
                        description: this.$t('tours.add-recipient.step_3.description'),
                        position: 'bottom'
                    }),

                    this.safeStep('#bin', {
                        title: this.$t('tours.add-recipient.step_4.title'),
                        description: this.$t('tours.add-recipient.step_4.description'),
                        position: 'bottom',
                        onNextClick: () => {
                            const iinTab = document.querySelector('#tab-iin');
                            if (iinTab) {
                                iinTab.click();
                            }

                            setTimeout(() => {
                                const idNo = document.querySelector('#input-id_no');
                                const lastName = document.querySelector('#input-lastName');

                                if (idNo) idNo.value = '030806550284';
                                if (lastName) lastName.value = 'Иванов';

                                idNo?.dispatchEvent(new Event('input', { bubbles: true }));
                                lastName?.dispatchEvent(new Event('input', { bubbles: true }));

                                const searchBtn = document.querySelector('button.btn.btn-primary.mx-0');
                                if (searchBtn) searchBtn.click();

                                tour.moveNext()
                            }, 200);
                        }
                    }),

                    this.safeStep('#iin-pane', {
                        title: this.$t('tours.add-recipient.step_5.title'),
                        description: this.$t('tours.add-recipient.step_5.description'),
                        position: 'bottom'
                    }),    
                    
                    {
                        element: "#open-add-recipient",
                        popover: {
                            title: this.$t('tours.add-recipient.step_6.title'),
                            description: this.$t('tours.add-recipient.step_6.description'),
                            position: 'bottom',
                            onNextClick: async () => {
                                if(tour.isLastStep()) {
                                    await this.$api.completeTour('add-recipient')
                                    this.showBitrixWidget()
                                    tour.destroy()
                                }
                                else {
                                    tour.moveNext()
                                }
                            }
                        }
                    }  
                ].filter(Boolean)

                const tour = driver({
                    showProgress: true,
                    progressText: '{{current}} / {{total}}',
                    allowClose: true,
                    disableActiveInteraction: true,
                    overlayOpacity: 0.65,
                    animate: true,
                    smoothScroll: true,
                    nextBtnText: this.$t('tours.popover.btn-next'),
                    prevBtnText: this.$t('tours.popover.btn-prev'),
                    doneBtnText: this.$t('tours.popover.btn-done'),
                    steps
                })

                tour.drive()
            },
            safeStep(elementSelector, popover) {
                if (document.querySelector(elementSelector)) {
                    return { element: elementSelector, popover }
                }
                return null
            },
            async fetchTourRoutes() {
                try {
                    const res = await this.$api.getTourRoutes()

                    if (res.result === 'SUCCESS') {
                        this.TOUR_ROUTES = res.data
                    }
                } catch (e) {
                    console.error('Failed to fetch tour routes:', e)
                }
            },
            async initTour(tourName, isModalTour = false) {   
                // Timeout exists due to some DOM elements not been loaded before tour starts, which breaks some steps
                await nextTick()
                setTimeout(async () => {
                    if (document.body.classList.contains('modal-open') && !isModalTour) {
                        this.pendingTour = tourName

                        return
                    }
                    this.pendingTour = null

                    try {
                        const r = await this.$api.startTour(tourName)

                        if (r.result === 'SUCCESS') {
                            switch (tourName) {
                                case 'profile':
                                    this.startProfileTour()
                                    break
                                case 'claims':
                                    this.startClaimsTour()
                                    break
                                case 'add-recipient':
                                    this.startAddRecipientTour()
                                    break
                                default:
                                    console.warn(`No tour method defined for "${tourName}"`)
                            }
                        }
                    } catch (e) {
                        console.error('Failed to start tour:', e)
                    }
                }, 250)
            },
            handleResize(e) {
                this.isMobile = e.matches
            },
            hideBitrixWidget() {
                const hide = () => {
                    const el = document.querySelector('.b24-widget-button-wrapper')
                    if (el) {
                        el.style.display = 'none'
                        return true
                    }
                    return false
                }

                if (!hide()) {
                    const obs = new MutationObserver(() => {
                        if (hide()) obs.disconnect()
                    })

                    obs.observe(document.body, { childList: true, subtree: true })
                }
            },

            showBitrixWidget() {
                const el = document.querySelector('.b24-widget-button-wrapper')
                if (el) {
                    el.style.display = ''
                }
            }
        },
        watch: {
            $route: {
                immediate: true,
                handler(to, from) {
                    if(this.$store.state.user) {
                        if(!('isCabinet' in to.meta) || !to.meta.isCabinet) return
                        
                        let settings = this.$store.state.user.settings ?? {}

                        if(to.path != '/settings/profile' && (settings.phone == '' || (!settings.address_id && !settings.address) || settings.validEmailConfirm == 0)) {
                            this.$router.push('/settings/profile')

                            this.$notify({
                                title: this.$t('notify.types.warn'),
                                text: this.$t('settings.profile.notify.need-fill-profile'),
                                type: 'warn'
                            })
                        }
                        else {
                            const path = this.$route.path.replace(/\/$/, '')
                            const tourName = this.TOUR_ROUTES[path]

                            if (this.$store.state.user && tourName) {
                                this.initTour(tourName)
                            }
                        }
                    }
                    
                    this.applyBlurState()                    
                }
            }
        },
        mounted() {
            this.toBlur()

            this.mediaQuery = window.matchMedia('(max-width: 991px)')

            this.isMobile = this.mediaQuery.matches

            this.mediaQuery.addEventListener('change', this.handleResize)

            this.fetchTourRoutes()

            document.addEventListener('hidden.bs.modal', () => {
                setTimeout(() => {
                    const anyModalStillOpen = document.querySelector('.modal.show')

                    if (anyModalStillOpen) {
                        setTimeout(() => {
                            if (document.querySelector('.modal.show')) return

                            if (this.pendingTour) {
                                const tour = this.pendingTour
                                this.pendingTour = null
                                this.initTour(tour)
                            }
                        }, 150)

                        return
                    }

                    if (this.pendingTour) {
                        const tour = this.pendingTour
                        this.pendingTour = null
                        this.initTour(tour)
                    }
                }, 350)
            })

            document.addEventListener('shown.bs.modal', (e) => {
                const modalEl = e.target

                if (modalEl.id === 'searchRecipientModal') {
                    setTimeout(() => {
                        this.initTour('add-recipient', true)
                    }, 150)
                }
            })
        },
        beforeUnmount() {
            this.mediaQuery.removeEventListener('change', this.handleResize)
        },
        updated() {
            this.applyBlurState() // Reapply the blur state after component updates (e.g., navigation)
        }
    }
    
</script>
<style>
    :root {
        --kdp-primary: #a6a0fb;
    }

    #app {
        /* display: flex;
        flex-wrap: wrap; */
        color: black;
    }

    #content {
        margin-left: 280px;
        padding: 100px 3em 2em;
        background-color: #ffffff;
        overflow-y: auto;
    }

    #app td a {
        color: #a6a0fb;
    }

    #app .link-primary {
        color: #a6a0fb !important;
        cursor: pointer;
    }

    /* #app .btn:active {
        color: #ffffff;
        background-color: #a6a0fb;
        border-color: #ffffff;
    } */

    #app .btn-secondary {
        color: #ffffff;
        background-color: #a6a0fb;
    }

    #app .btn-secondary:hover {
        background-color: #cac6ff;
    }

    #app .btn-secondary:focus {
        background-color: #cac6ff;
        outline: 3px solid #eeeeee;
        border-radius: 4px;
    }

    #app .btn-primary {
        background-color: #3a3077;
    }

    #app .btn-primary:hover {
        background-color: #7d6ce0;
    }

    #app .btn-primary:focus {
        background-color: #7d6ce0;
        outline: 3px solid #cac6ff;
        border-radius: 4px;
    }
    
    #app .btn-primary:active {
        background-color: #6c56ea;
    }

    #app .btn-outline-primary {
        border-color: #867ac9;
        color: #6c56ea;
        border-radius: 4px;
    }

    #app .btn-outline-primary:hover {
        background-color: #867ac9;
        color: #e3e1ff;
    }
    #app .btn-outline-primary:focus {
        background-color: #867ac9;
        color: #e3e1ff;
        outline: 4px solid #6c56ea;
        border-radius: 4px;
    }

    #app .btn-outline-secondary {
        color: #a6a0fb;
        border-color: #eeeeee;
    }

    #app .btn-outline-secondary:hover {
        border-color: #a6a0fb;
    }

    #app .btn-link:hover,
    #app .btn-link:focus {
        outline: 2px solid #cac6ff;
    }

    #app .contacts-link {
        color: #3a3077;
    }

    #app .contacts-link:hover {
        color: #7d6ce0;
    }

    #app .form-control:focus,
    #app .form-select:focus {
        border-color: #a6a0fb;
        box-shadow: inset 0px 0px 0px 1px #a6a0fb;
    }

    #app .text-primary {
        color: #3a3077;
    }

    #app .buttonBack{
        color: #a6a0fb;
        background: none;
        position: absolute;
        top: 0;
        left: 0;
        font-size: 1.5rem;
        margin: .75rem;
        transition: 250ms;
        -moz-transition: 250ms;
        -o-transition: 250ms;
        -webkit-transition: 250ms;
    }

    #app .buttonBack:hover {
        color: #000000;
        background: none;
    }

    /* breadcrumbs */

    #app .am-breadcrumbs {
        margin: 0 0 1rem;
    }

    #app .am-breadcrumbs__link {
        color: #888888;
    }

    #app .am-breadcrumbs__link:not(.am-breadcrumbs__link_current):hover {
        color: #a6a0fb;
    }

    #app .bi-check-circle-fill {
        color: green;
    }
    
    #app .bi-question-circle-fill {
        color: rgb(255, 198, 41);
    }

    #app .bi-x-circle-fill {
        color: #dc3545;
    }

    #app .modal.fade {
        background: rgba(0, 0, 0, 0.25);
    }

    #app .modal-backdrop.fade {
        opacity: 0;
    }

    #app .my-notification {
        background-color: rgba(255, 255, 255, 0.85);
        color: #000000;
        border: 3px solid #a6a0fb;
        border-radius: 10px;
        padding: 1rem;
        margin: 1rem;
    }

    #app .template-body-text {
        font-size: 14px;
    }
    #app .template-body-text p {
        margin: 0;
        text-indent: 3em;
    }

    #app .template-body-text li {
        text-indent: 1em;
    }

    #app .text-bg-warning,
    .text-bg-info {
        color: #fff !important;
    }

    /* loading */

    #app .btn-loading {
        position: relative;
        overflow: hidden;
    }

    #app .btn-loading::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: block;
        width: 100%;
        height: 100%;
        background-color: rgba(166, 160, 251, 0.9);
        z-index: 1;
    }

    #app .btn-loading::after {
        content: '\F116';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: auto;
        color: #3a3077;
        font-family: bootstrap-icons;
        font-size: 1.5rem;
        animation: loading 1s linear 0s infinite;
        z-index: 2;
    }

    #app .blurred {
        filter: blur(5px);
        transition: filter 0.01s ease-in-out;
    }

    #app .blurred + .select2-container .select2-dropdown {
        filter: blur(5px);
    }

    /* For hiding instead of blurring */
    #app .hidden {
        display: none;
    }

    .driver-popover {
        padding: 0px !important;
    }

    .driver-popover-title {
        padding: 15px;
        background-color: #3a3077;
        color: white;
    }

    .driver-popover-description {
        padding: 15px 15px 0px 15px;
    }

    .driver-popover-footer {
        padding: 0px 15px 15px 15px;
    }

    .driver-popover-next-btn, .driver-popover-prev-btn {
        padding: 10px !important;
        background-color: #a6a0fb !important;
        color: white !important;
        text-shadow: none !important;
    }

    .driver-popover-next-btn {
        background-color: #3a3077 !important;
    }

    .loading::after {
        content: '\F116';
        display: inline-block;
        margin: -.56rem .5rem;
        color: #a6a0fb;
        font-family: bootstrap-icons;
        font-size: 1.5rem;
        animation: loading 1s linear 0s infinite;
    }

    .modal-footer .loading::after {
        font-size: 1em;
    }

    @keyframes loading {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }

    @media (max-width: 991px) {
        #content {
            margin-left: 0;
            padding: 2em 2em 70px;
        }
    }

    @media (max-width: 576px) {
        #app .vue-notification-group {
            box-sizing: border-box;
            width: calc(100vw - 2rem) !important;
            max-width: calc(100vw - 2rem);
            right: 1rem !important;
            left: auto !important;
            overflow-x: hidden;
        }

        #app .my-notification {
            box-sizing: border-box;
            width: 100%;
            max-width: 100%;
            margin: 0.5rem 0;
            transform: none;
        }

        #app .my-notification .row {
            margin-left: 0;
            margin-right: 0;
        }
    }
</style>
